import { loadAdvancedSearchQueryActions, SearchEngine } from '@coveo/headless';
import { ISelectedStatCodeData } from 'Foundation/StatCode/StatModels';
import { IUserData } from 'Foundation/User/UserContextModels';
import { getCoveoFieldNameByString, personalizedFieldMapper } from '../Mappers/CoveoFieldMapper';
import { PersonalizedFieldNames } from '../Models/CoveoModels';

// This method will update the headless engine with a new advanced query with a updated state
export function updateSearchAQStateFilters(headlessEngine: SearchEngine<{}>, newState: string) {
  let currentAQ: string | undefined = headlessEngine.state.advancedSearchQueries?.aq;
  if (currentAQ && currentAQ !== '') {
    let aq: string = updateAQStateFilter(newState, currentAQ);
    const advQuery = loadAdvancedSearchQueryActions(headlessEngine);
    const action = advQuery.registerAdvancedSearchQueries({ aq: aq });
    headlessEngine.dispatch(action);
  }
}
// This function will take the existing advanced query to find the state specific filter and update it: (NOT @lmcstates OR @lmcstates=="CA")
function updateAQStateFilter(userState: string, currentAQ: string): string {
  let aq = currentAQ;
  // Inside existing aq Look for (NOT @lmcstates OR @lmcstates=="CA")
  let re = new RegExp(
    ' \\(NOT @' +
      getCoveoFieldNameByString(PersonalizedFieldNames.states)?.coveoFieldName +
      ' OR @' +
      getCoveoFieldNameByString(PersonalizedFieldNames.states)?.coveoFieldName +
      '=="[A-Z]{2}"\\)',
  );
  // Remove state filter if found
  if (currentAQ.match(re)) {
    aq = currentAQ.replace(re, '');
  }
  // Re-add state filter if a state passed in
  if (userState.toUpperCase() !== 'ALL' && userState !== '') {
    aq += createStateFilter(userState);
  }
  return aq;
}

// This will return the filter for omitting archived content
export function getArchivedFilter(archived: boolean): string {
  let archivedQuery = ` (NOT @${getCoveoFieldNameByString(PersonalizedFieldNames.archived)
    ?.coveoFieldName} OR @${getCoveoFieldNameByString(PersonalizedFieldNames.archived)?.coveoFieldName}=="${
    archived ? '1' : '0'
  }")`;
  if (archived)
    //if filtering for archived content, default content type to guides
    return (
      archivedQuery +
      ` (NOT @${getCoveoFieldNameByString(PersonalizedFieldNames.contenttype)
        ?.coveoFieldName} OR @${getCoveoFieldNameByString(PersonalizedFieldNames.contenttype)
        ?.coveoFieldName}=="Product Guide")`
    );
  return archivedQuery;
}

export function getPublishDateFilter(): string {
  return ' (NOT @lmcpublishdate OR @lmcpublishdate<=now)';
}
// This will return the state filter that should be added to the aq
function createStateFilter(state: string): string {
  return (
    ' (NOT @' +
    getCoveoFieldNameByString(PersonalizedFieldNames.states)?.coveoFieldName +
    ' OR @' +
    getCoveoFieldNameByString(PersonalizedFieldNames.states)?.coveoFieldName +
    '=="' +
    state.toUpperCase() +
    '")'
  );
}
// Create a default state filter that uses a user's currently selected state:
// (NOT @lmcstates OR @lmcstates=="CA")
export function getDefaultUserStateFilter(userData: IUserData): string {
  if (userData && userData.state && userData.state !== '') {
    return createStateFilter(userData.state);
  }
  return '';
}
export function getUserQueryFilters(userData: IUserData): string {
  let qf = '';
  for (const personalizedField of personalizedFieldMapper) {
    if (personalizedField.advancedQuery && personalizedField.userObjPropName) {
      let userField = undefined;
      if (personalizedField.userObjPropName.length === 1)
        userField = getProperty(userData, personalizedField.userObjPropName[0] as keyof IUserData);
      else
        userField = getProperty(
          userData.selectedStatCodeData,
          personalizedField.userObjPropName[1] as keyof ISelectedStatCodeData,
        );
      if (userField) {
        if (personalizedField.fieldType === 'bool') {
          qf +=
            ' (NOT @' +
            personalizedField.coveoFieldName +
            ' OR @' +
            personalizedField.coveoFieldName +
            '=="' +
            userField +
            '")';
        } else if (personalizedField.fieldType === 'string') {
          // This code is for personalization rules that are configured for 'is not X'
          // Author: Role is not CSR
          // becomes
          // Coveo: @role="is not" AND NOT @rule=="is not CSR"
          // The result of this means that users who are CSRs will have all content return where @role contains "is not" but not "is not CSR"
          qf +=
            ' (NOT @' +
            personalizedField.coveoFieldName +
            ' OR @' +
            personalizedField.coveoFieldName +
            '=="' +
            userField +
            '"';
          qf +=
            ' OR (@' +
            personalizedField.coveoFieldName +
            '="is not" AND NOT @' +
            personalizedField.coveoFieldName +
            '=="is not ' +
            userField +
            '"))';
        }
      }
    }
  }
  return qf;
}

function getProperty<T, K extends keyof T>(o: T, propertyName: K): T[K] | undefined {
  try {
    return o[propertyName];
  } catch {}
  return undefined;
}
